import { useCallback, useEffect, useRef, useState } from 'react'

import axios from '../../dataClient/dataClient'
import { REQUEST_SEARCH_QUERY_LENGTH } from '../../util/consts'
import { useWorkflowPoll, WorkflowPollCancelledError, WorkflowPollTimeoutError, DEFAULT_WORKFLOW_POLL_INTERVAL_MS } from './useWorkflowPoll'

export const SEARCH_STATUS = {
  idle: 'idle',
  searching: 'searching',
  results: 'results',
  error: 'error'
}

export const SEARCH_POLL_INTERVAL_MS = DEFAULT_WORKFLOW_POLL_INTERVAL_MS
const SEARCH_POLL_TIMEOUT_MS = 2 * 60 * 1000
export const GENERIC_SEARCH_ERROR = 'Hm, the search could not be completed. Try again.'
export const TIMEOUT_SEARCH_ERROR = 'The search is taking too long. Try again in a moment.'

// Keep simple validation errors client-side; the server remains authoritative.
export function validateSearchQuery(query) {
  const trimmed = (query ?? '').trim()
  if (trimmed.length < REQUEST_SEARCH_QUERY_LENGTH.min) {
    return `Enter at least ${REQUEST_SEARCH_QUERY_LENGTH.min} characters to search.`
  }
  if (trimmed.length > REQUEST_SEARCH_QUERY_LENGTH.max) {
    return `Search terms must be at most ${REQUEST_SEARCH_QUERY_LENGTH.max} characters.`
  }
  return null
}

export const searchStorageKey = foiId => `requestSearch:${foiId}`

function readStoredSearch(foiId) {
  try {
    const raw = window.sessionStorage.getItem(searchStorageKey(foiId))
    if (!raw) return null
    const stored = JSON.parse(raw)
    return stored && typeof stored.query === 'string' && typeof stored.workflowId === 'string' ? stored : null
  } catch {
    return null
  }
}

function writeStoredSearch(foiId, value) {
  try {
    if (value) window.sessionStorage.setItem(searchStorageKey(foiId), JSON.stringify(value))
    else window.sessionStorage.removeItem(searchStorageKey(foiId))
  } catch {
    // Search still works when session storage is unavailable.
  }
}

const initialState = {
  status: SEARCH_STATUS.idle,
  query: '',
  workflowId: null,
  results: [],
  totals: null,
  indexing: { pending: 0, failed: 0 },
  errorMessage: null,
  validationMessage: null
}

// Starts a request search and follows its workflow to completion.
export function useRequestSearch(foiId, { pollIntervalMs = SEARCH_POLL_INTERVAL_MS } = {}) {
  const [state, setState] = useState(() => {
    const stored = readStoredSearch(foiId)
    return stored ? { ...initialState, status: SEARCH_STATUS.searching, query: stored.query, workflowId: stored.workflowId } : initialState
  })
  const { poll, cancel } = useWorkflowPoll({ intervalMs: pollIntervalMs, timeoutMs: SEARCH_POLL_TIMEOUT_MS })
  // Ignore results from superseded searches.
  const activeWorkflowRef = useRef(state.workflowId)
  const searchStartedHereRef = useRef(false)

  const fail = useCallback((workflowId, message) => {
    setState(prev =>
      prev.workflowId === workflowId ? { ...prev, status: SEARCH_STATUS.error, errorMessage: message || GENERIC_SEARCH_ERROR } : prev
    )
  }, [])

  const fetchSearch = useCallback(async workflowId => (await axios.get(`/foi/${foiId}/search/${workflowId}`)).data.data, [foiId])

  const follow = useCallback(
    async workflowId => {
      try {
        const data = await poll(workflowId, { fetch: fetchSearch })
        if (activeWorkflowRef.current !== workflowId) return
        if (data.status === 'SUCCEEDED') {
          setState(prev =>
            prev.workflowId === workflowId
              ? {
                  ...prev,
                  status: SEARCH_STATUS.results,
                  results: data.results ?? [],
                  totals: data.totals ?? { results: (data.results ?? []).length, files: 0 },
                  indexing: data.indexing ?? prev.indexing
                }
              : prev
          )
        } else {
          writeStoredSearch(foiId, null)
          fail(workflowId, GENERIC_SEARCH_ERROR)
        }
      } catch (err) {
        if (err instanceof WorkflowPollCancelledError) return
        if (activeWorkflowRef.current !== workflowId) return
        console.error('Request search polling error:', err)
        // Silently discard stale searches restored from session storage.
        if (err.response?.status === 404 && !searchStartedHereRef.current) {
          writeStoredSearch(foiId, null)
          activeWorkflowRef.current = null
          setState(initialState)
          return
        }
        writeStoredSearch(foiId, null)
        fail(workflowId, err instanceof WorkflowPollTimeoutError ? TIMEOUT_SEARCH_ERROR : GENERIC_SEARCH_ERROR)
      }
    },
    [poll, fetchSearch, foiId, fail]
  )

  const search = useCallback(
    async rawQuery => {
      const validationMessage = validateSearchQuery(rawQuery)
      if (validationMessage) {
        setState(prev => ({ ...prev, validationMessage }))
        return
      }
      const query = rawQuery.trim()
      const attempt = {}
      searchStartedHereRef.current = true
      activeWorkflowRef.current = attempt
      cancel()
      setState({ ...initialState, status: SEARCH_STATUS.searching, query })

      try {
        const response = await axios.post(`/foi/${foiId}/search`, { query })
        if (activeWorkflowRef.current !== attempt) return
        const { workflowId, indexing } = response.data.data
        activeWorkflowRef.current = workflowId
        writeStoredSearch(foiId, { query, workflowId })
        setState(prev => ({ ...prev, workflowId, indexing: indexing ?? initialState.indexing }))
        follow(workflowId)
      } catch (err) {
        if (activeWorkflowRef.current !== attempt) return
        activeWorkflowRef.current = null
        if (err.response?.status === 400) {
          setState({ ...initialState, query, validationMessage: err.response.data?.message || validateSearchQuery('') })
        } else {
          console.error('Request search failed to start:', err)
          setState({ ...initialState, status: SEARCH_STATUS.error, query, errorMessage: GENERIC_SEARCH_ERROR })
        }
      }
    },
    [foiId, follow, cancel]
  )

  const restoredWorkflowId = useRef(state.workflowId)
  useEffect(() => {
    if (restoredWorkflowId.current) follow(restoredWorkflowId.current)
  }, [])

  const retry = useCallback(() => {
    if (state.query) search(state.query)
  }, [search, state.query])

  const clear = useCallback(() => {
    activeWorkflowRef.current = null
    cancel()
    writeStoredSearch(foiId, null)
    setState(initialState)
  }, [foiId, cancel])

  const clearValidation = useCallback(() => {
    setState(prev => (prev.validationMessage ? { ...prev, validationMessage: null } : prev))
  }, [])

  return { ...state, search, retry, clear, clearValidation }
}

export default useRequestSearch
